import type { Tone } from '../tone';
import { DUE_LABEL, DUE_TONE, dueKey, isLate } from '../time/dueDate';

/** What a kit's row or chip should say, and in which tone. Section 12. */
export interface KitStatus {
  tone: Tone;
  label: string;
}

/**
 * The status of a kit on the floor.
 *
 * Order matters and is the client's priority, not ours. Urgent is an
 * instruction from the dentist and outranks everything. Rework is next: the
 * case has already failed once. Only then does the calendar get a say.
 *
 * Urgent, Rework and Overdue are said together when more than one is true, in
 * that order, so no word hides another: "Urgent · Overdue", "Rework · Overdue"
 * for a rework case past its date, "Urgent · Rework · Overdue" for a case that
 * is all three. The tone is the first word's. Urgent with Overdue is the
 * request of 8 October 2026; Rework with them is the lab's answer of the same
 * day, when a late rework case still read Rework and nothing else.
 *
 * Then the due date, by the one rule in src/domain/time/dueDate.ts: Overdue
 * in red once the date has passed, Due today and Due tomorrow in amber, and
 * On track for anything later. The request of 7 October 2026, section 6. It
 * reads the calendar day and `today`, the lab's day, never the working days
 * left: a case due on Sunday is not due tomorrow on a Thursday.
 *
 * `finished` is a tray at Ready: its work is done, and a finished case is
 * never overdue, whatever the date says.
 */
export function kitStatus(
  kit: {
    urgent: boolean;
    rework: boolean;
    deliveryDate: string;
    finished?: boolean;
  },
  today: string,
): KitStatus {
  if (kit.urgent || kit.rework) {
    const late = isLate(kit.deliveryDate, today, kit.finished === true);
    const words = [
      kit.urgent ? 'Urgent' : null,
      kit.rework ? 'Rework' : null,
      late ? DUE_LABEL.overdue : null,
    ].filter((word): word is string => word !== null);
    return { tone: kit.urgent ? 'urgent' : 'rework', label: words.join(' · ') };
  }

  const due = dueKey(kit.deliveryDate, today);
  if (due === 'later' || (due === 'overdue' && kit.finished === true)) {
    return { tone: 'ok', label: 'On track' };
  }
  return { tone: DUE_TONE[due], label: DUE_LABEL[due] };
}
